<template>
  <div class="store-maplist">
    <div class="google-map-result font-sans" :class="{ 'rfp-stlc-hide-map': !displayMap }">
      <a id="skip-map" href="#after-map">Skip the map</a>
      <div id="map">Map</div>
      <span id="after-map">After Map</span>
    </div>
    <rfp-storelocator-map-popup
      :render-popup="renderPopup"
      :location="location"
      :distance-enabled="distanceEnabled"
      :hide-get-direction-link="hideGetDirectionLink == true"
      :distance-type="distanceType"
      :get-directions="getDirections"
      :get-direction-aria-label="getDirectionAriaLabel"
    />
  </div>
</template>

<script setup>
/**
 * HACK:
 * This empty script is required to make nested components work in Vue 3
 * while keeping compatibility with Vue 2 intact.
 */
</script>

<script>
import eventBus from '@refapp/eventbus-js';

/* global google */

/**
 * @module Component/GoogleMapx
 * @desc the GoogleMap tag implement
 */
import GoogleAPI from '../mixins/rfp-storelocator-google-api';
import datalayerMixin from '../mixins/datalayer-mixin';
import { tryParseJson } from '../mixins/utils';
import rfpStorelocatorMapPopup from '../map-popup/rfp-storelocator-map-popup.vue';
import { DisplayMode } from '../mixins/constants';

export default {
  /**
   * @member {type} name tag name of the component
   * @readonly
   */
  name: 'rfp-storelocator-google-map',
  components: {
    'rfp-storelocator-map-popup': rfpStorelocatorMapPopup,
  },
  mixins: [GoogleAPI, datalayerMixin],
  props: {
    latitude: {
      type: String,
      required: true,
    },
    longitude: {
      type: String,
      required: true,
    },
    zoomLevel: {
      type: Number,
      required: true,
    },
    mapUrl: {
      type: String,
      required: true,
    },
    defaultCountry: {
      type: String,
      required: false,
      default: '',
    },
    iriEnabled: {
      type: Boolean,
      required: false,
      default: true,
    },
    clickableIcons: {
      type: Boolean,
      required: false,
      default: false,
    },
    upcCode: {
      type: String,
      required: false,
      default: '',
    },
    storesPerPage: {
      type: Number,
      required: true,
    },
    defaultRadius: {
      type: String,
      required: true,
    },
    defaultDistanceMetric: {
      type: String,
      required: true,
    },
    filterByDistanceEnable: {
      type: Boolean,
      required: true,
    },
    getDirections: {
      type: String,
      required: false,
      default: '',
    },
    showHideGooglemapText: {
      type: String,
      required: false,
      default: '',
    },
    hideGetDirectionLink: {
      type: Boolean,
      default: false,
    },
    getDirectionTags: {
      type: String,
      required: false,
      default: '',
    },
    storeClickTags: {
      type: String,
      required: false,
      default: '',
    },
    callStoreTags: {
      type: String,
      required: false,
      default: '',
    },
    bookStoreTags: {
      type: String,
      required: false,
      default: '',
    },
    socialInteractionTags: {
      type: String,
      required: false,
      default: '',
    },
    autocompleteType: {
      type: String,
      required: true,
      default: '',
    },
    getDirectionAriaLabel: {
      type: String,
      default: 'Get directions to [location]',
    },
    displayMode: {
      type: String,
      required: false,
      default: DisplayMode.List,
      validator(value) {
        return Object.values(DisplayMode).includes(value);
      },
    },
    languageCode: {
      type: String,
      required: true,
    },
  },
  /**
   * @function data
   * @desc init the data when the component mount
   */
  data() {
    return {
      resultCount: -1, // list hasn't loaded, positive value or 0 after
      storeDetails: [],
      storeStatus: false,
      item: [],
      test: '',
      showToggle: false,
      displayMap: true,
      distanceEnabled: false,
      categoryEnabled: '',
      typemodel: '',
      productGroupId: '',
      productGroupEnabled: '',
      distances: '',
      distanceTypes: '',
      zip: '',
      upc: '',
      pageNo: 1,
      filterByAttribute: [],
      location: {},
      renderPopup: false,
    };
  },
  /**
   * @function created
   * @desc on mounting , listen the other component Event
   */
  created() {
    eventBus.on('toggleMap', (value) => {
      this.displayMap = value;
    });
    // Create a media condition that targets viewports at least 768px wide
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    if (mediaQuery.matches) {
      this.showToggle = true;
      this.displayMap = this.displayMode !== DisplayMode.List;
    }

    // Assign the default lat and lon value
    this.lat = this.latitude.replace(/,/g, '.');
    this.lon = this.longitude.replace(/,/g, '.');
    this.distanceTypes = this.defaultDistanceMetric;
    this.distances = this.defaultRadius;
    this.typemodel = 'All';
    this.mapConfig.zoom = Number(this.zoomLevel);
    this.mapConfig.clickableIcons = this.clickableIcons === true;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = this.mapUrl;
    document.body.appendChild(script);
    script.onload = async () => await this.init(true);

    const latLng = {
      lat: this.lat,
      lon: this.lon,
      typemodel: this.typemodel,
      productGroupId: this.productGroupId,
      distances: this.distances,
      distanceTypes: this.distanceTypes,
      pageNo: 1,
    };
    eventBus.emit('promotionMapData', latLng);

    eventBus.on('productGroupId-updated', (value) => {
      (this.productGroupId = value.productGroupId),
        (this.productGroupEnabled = value.productGroupEnabled);
    });
    eventBus.on('storeMsg', () => {
      this.listStatus = 'none';
      this.storeStatus = true;
    });
    eventBus.on('mapData', async (value) => {
      this.lat = value.lat;
      this.lon = value.lon;
      this.typemodel = value.typemodel;
      this.distances = value.distances;
      this.distanceTypes = value.distanceTypes;

      if (typeof value.distanceEnabled === 'boolean') {
        this.distanceEnabled = value.distanceEnabled;
      }

      this.categoryEnabled = value.categoryEnabled;
      this.productGroupEnabled = value.productGroupEnabled;
      this.zip = value.zip;
      this.upc = value.upc;
      this.pageNo = value.pageNo;
      this.productGroupId = value.productGroupId;
      this.filterByAttribute = value.filterByAttribute;
      await this.init(false);
    });
    eventBus.on('presetFilters', (value) => {
      if (value.distanceTypes) {
        this.distanceTypes = value.distanceTypes;
      }

      if (typeof value.distanceEnabled === 'boolean') {
        this.distanceEnabled = value.distanceEnabled;
      }

      if (value.filterByAttribute) {
        this.filterByAttribute = value.filterByAttribute;
      }

      if (value.productGroupId) {
        this.productGroupId = value.productGroupId;
      }

      if (value.productGroupEnabled) {
        this.productGroupEnabled = value.productGroupEnabled;
      }

      if (value.distances) {
        this.distances = value.distances;
      }

      if (value.typemodel) {
        this.typemodel = value.typemodel;
      }
    });
    eventBus.emit('rfp-after-loaded-page');

    eventBus.on('pagination_record_count', (datas) => {
      this.resultCount = datas.TotalStoreCount;
    });

    // listen the storeData event from other mixins
    eventBus.on('storeData', ({ value, page }) => {
      this.storeDetails = value;
      eventBus.emit('pagination', { value, page });
      if (
        document.getElementsByClassName('salonstore-list') &&
        document.getElementsByClassName('salonstore-list').length > 0
      ) {
        if (this.resultCount > 0) {
          // It's rendering based on search data
          eventBus.emit('updateMarker', value);
          if (this.storeStatus) {
            this.listStatus = 'block';
          }
        } else {
          // It's rendering default based on data
          this.listStatus = 'block';
          eventBus.emit('salonList');
        }
      } else if (this.storeDetails.length > 0) {
        // It's rendering only for storelocatorpdp
        eventBus.emit('updateMarker', value);
        this.listStatus = 'block';
      } else {
        this.resetMarkers();
      }
    });

    eventBus.on('rfpTaggingStoreClick', (storeName) => {
      eventBus.emit('rfpTaggingPushToDatalayer', {
        tags: tryParseJson(this.storeClickTags),
        values: {
          storeName: storeName,
          zipcodeOrCity: this.zipcodeOrCity,
        },
      });
    });
    eventBus.on('rfpTaggingBookSaloon', (storeName) => {
      eventBus.emit('rfpTaggingPushToDatalayer', {
        tags: tryParseJson(this.bookStoreTags),
        values: {
          storeName: storeName,
        },
      });
    });
    eventBus.on('rfpTaggingCallStore', (storeName) => {
      eventBus.emit('rfpTaggingPushToDatalayer', {
        tags: tryParseJson(this.callStoreTags),
        values: {
          storeName: storeName,
        },
      });
    });
    eventBus.on('rfpTaggingSocialInteraction', (eventData) => {
      eventBus.emit('rfpTaggingPushToDatalayer', {
        tags: tryParseJson(this.socialInteractionTags),
        values: {
          storeName: eventData.storeName,
          socialNetwork: eventData.socialNetwork,
          socialTarget: eventData.socialTarget,
        },
      });
    });
    eventBus.on('rfpTaggingGetDirections', (storeName) => {
      eventBus.emit('rfpTaggingPushToDatalayer', {
        tags: tryParseJson(this.getDirectionTags),
        values: {
          storeName: storeName,
        },
      });
    });
  },
  methods: {
    async init(status) {
      // NOTE: this.initMap is exposed by the GoogleAPI mixin
      await this.initMap();

      // TODO: Stop sending the full map object as an event payload
      eventBus.emit('map-options', this.map);

      let defaultCountryCode = this.defaultCountry;
      let input = document.getElementById('txtZIPCODE');

      // Disable Autosuggestion if IRI is enabled, as this api supports only search by ZIP code
      if (!this.iriEnabled) {
        var autocomplete = new google.maps.places.Autocomplete(input);
        if (
          defaultCountryCode == '' ||
          defaultCountryCode == undefined ||
          defaultCountryCode == null
        ) {
          autocomplete.bindTo('bounds', this.map);
        } else {
          autocomplete.setComponentRestrictions({ country: defaultCountryCode.split(',') });
          autocomplete.setFields(['address_components', 'geometry']);
          autocomplete.setTypes([this.autocompleteType]);
        }
      }

      if (status) {
        eventBus.emit('currentPlace');
      }
      this.bindGMapListeners(true);
    },
  },
};
</script>
